import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import ViewToggle, { readViewPreference } from '../../../components/common/ViewToggle';
import CourseNameBadge from '../../../components/common/CourseNameBadge';
import BatchesAnalytics from '../../../components/analytics/BatchesAnalyticsV2';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const STATUS_COLORS = { upcoming:'primary', started:'info', ongoing:'success', completed:'secondary', discontinued:'warning', cancelled:'danger' };

const BSModal = ({ show, onClose, title, size='', children, footer }) =>
  show ? (
    <div className="modal fade show d-block" style={{ background:'rgba(0,0,0,0.45)', zIndex:1050 }}
      onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div className={`modal-dialog modal-dialog-scrollable ${size} modal-dialog-centered`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

const blankBatch = () => ({
  course:'', batchNumber:'', faculty:'', facultyName:'', batchType:'group',
  classType:'', classroom:'', useFacultyInput:false,
  timingStartTime:'09:00', timingEndTime:'11:00',
  startDate:'', endDate:'', maxStudents:'30', status:'upcoming', notes:'',
  schedule:[],
});

export default function Batches() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const roleKey = user?.roleKey || '';
  const canWrite = isSuperAdmin || ['admin','manager','team_lead'].includes(roleKey);
  const canSeeAnalytics = canWrite;

  // View toggle (table ↔ analytics), persisted per-user.
  const [view, setView] = useState(() => readViewPreference('batches', 'table'));

  const [batches, setBatches]   = useState([]);
  const [stats, setStats]       = useState({});
  const [courses, setCourses]   = useState([]);
  const [faculty, setFaculty]   = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    batch: '',
    course: '',
    faculty: '',
    dateFrom: '',
    dateTo: '',
    schedule: '',
    students: '',
    progress: '',
    status: '',
  });
  const [activeHeaderFilter, setActiveHeaderFilter] = useState('');
  const [yearTab, setYearTab]   = useState('all');
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const LIMIT = 20;

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blankBatch());
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');
  const [fetchingBatchNum, setFetchingBatchNum] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [progressBatch, setProgressBatch] = useState(null);
  const [progressData, setProgressData]   = useState(null);
  const [showProgress, setShowProgress]   = useState(false);
  const [expandedMods, setExpandedMods]   = useState({});

  const [showSettings, setShowSettings]   = useState(false);
  const [settingsTab, setSettingsTab]     = useState('classTypes');
  const [ctForm, setCtForm] = useState({ value:'', label:'', daysPerWeek:5 });
  const [crForm, setCrForm] = useState({ name:'', capacity:30 });
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [toast, setToast]               = useState({ show:false, message:'', type:'success' });

  const notify = (message, type='success') => setToast({ show:true, message, type });

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:LIMIT };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      const [r, s] = await Promise.all([
        api.get('/training/batches', { params }),
        api.get('/training/batches/stats/overview').catch(() => null),
      ]);
      setBatches(r.data.data || []);
      setTotal(r.data.pagination?.total || 0);
      if (s) setStats(s.data.data ?? s.data ?? {});
    } catch(_) {}
    setLoading(false);
  }, [page, search, filterStatus]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const loadCourses = useCallback(() => {
    return api.get('/training/courses', { params: { status: 'active', limit: 0 } })
      .then(r => setCourses(r.data.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadCourses();
    // Dedicated endpoint: accessible to admin, manager AND team_lead without
    // requiring the broader 'users.view' permission. Matches both primary
    // roleKey and additionalRoleKeys so multi-role users (e.g. a team_lead
    // who also teaches) appear in the picker.
    api.get('/training/batches/faculty-list')
       .then(r => setFaculty(r.data.records || []))
       .catch(()=>{});
    loadSettings();
  }, [loadCourses]);

  const loadSettings = async () => {
    try {
      const [ct, cr] = await Promise.all([
        api.get('/training/batch-settings/class-types'),
        api.get('/training/batch-settings/classrooms'),
      ]);
      // Backend wraps arrays under `.records` (see utils/response.js); keep
      // `.data` as fallback in case some environment returns the legacy shape.
      setClassTypes(ct.data.records || ct.data.data || []);
      setClassrooms(cr.data.records || cr.data.data || []);
    } catch(_) {}
  };

  // Batch years for tab filtering
  const batchYears = [...new Set(batches.map(b => b.startDate ? new Date(b.startDate).getFullYear() : null).filter(Boolean))].sort().reverse();
  const filteredBatches = batches.filter(b => {
    if (yearTab !== 'all') {
      const y = b.startDate ? String(new Date(b.startDate).getFullYear()) : null;
      if (y !== yearTab) return false;
    }
    const batchText = `${b.batchNumber || ''} ${b.classroom || ''}`.toLowerCase();
    const courseText = `${b.course?.courseName || ''} ${b.course?.courseCode || ''}`.toLowerCase();
    const facultyText = `${b.faculty ? `${b.faculty.firstName || ''} ${b.faculty.lastName || ''}` : (b.facultyName || '')}`.toLowerCase();
    const scheduleText = `${b.timing?.startTime || ''} ${b.timing?.endTime || ''}`.toLowerCase();
    const studentsText = `${b.currentStudents || 0}/${b.maxStudents || 30}`.toLowerCase();
    const progressText = `${b._progressPct ?? ''}`.toLowerCase();
    const statusText = `${b.status || ''}`.toLowerCase();
    const startDateOnly = b.startDate ? new Date(b.startDate).toISOString().split('T')[0] : '';
    const endDateOnly = b.endDate ? new Date(b.endDate).toISOString().split('T')[0] : '';

    if (columnFilters.batch && !batchText.includes(columnFilters.batch.toLowerCase())) return false;
    if (columnFilters.course && !courseText.includes(columnFilters.course.toLowerCase())) return false;
    if (columnFilters.faculty && !facultyText.includes(columnFilters.faculty.toLowerCase())) return false;
    if (columnFilters.dateFrom && (!startDateOnly || startDateOnly < columnFilters.dateFrom)) return false;
    if (columnFilters.dateTo && (!endDateOnly || endDateOnly > columnFilters.dateTo)) return false;
    if (columnFilters.schedule && !scheduleText.includes(columnFilters.schedule.toLowerCase())) return false;
    if (columnFilters.students && !studentsText.includes(columnFilters.students.toLowerCase())) return false;
    if (columnFilters.progress && !progressText.includes(columnFilters.progress.toLowerCase())) return false;
    if (columnFilters.status && !statusText.includes(columnFilters.status.toLowerCase())) return false;
    return true;
  });

  const openNew = () => {
    // Refresh courses + faculty so items created elsewhere since the page loaded
    // appear in the dropdowns. Backgrounded — dropdown will populate as soon as
    // the fetch resolves.
    loadCourses();
    setEditing(null); setForm(blankBatch()); setSelectedCourse(null); setFormError(''); setShowForm(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    const c = courses.find(c => c._id === (b.course?._id||b.course));
    setSelectedCourse(c || null);
    setForm({
      course:        b.course?._id || b.course || '',
      batchNumber:   b.batchNumber || '',
      faculty:       b.faculty?._id || b.faculty || '',
      facultyName:   b.facultyName || '',
      useFacultyInput: !b.faculty && !!b.facultyName,
      batchType:     b.batchType || 'group',
      classType:     b.classType || '',
      classroom:     b.classroom || '',
      timingStartTime: b.timing?.startTime || '09:00',
      timingEndTime:   b.timing?.endTime   || '11:00',
      startDate:     b.startDate ? new Date(b.startDate).toISOString().split('T')[0] : '',
      endDate:       b.endDate   ? new Date(b.endDate).toISOString().split('T')[0]   : '',
      maxStudents:   b.maxStudents || 30,
      status:        b.status || 'upcoming',
      notes:         b.notes || '',
      schedule:      b.schedule || [],
    });
    setFormError(''); setShowForm(true);
  };

  // Compute end date from a start date + course duration + class-type days-per-week.
  //
  // Priority:
  //   1. If a specific classType is chosen with daysPerWeek, derive total sessions
  //      from course.totalHours / session hours, then spread them over the schedule.
  //   2. Otherwise fall back to course.numberOfMonths (same rule as the backend).
  //
  // Returns YYYY-MM-DD string or '' if we can't compute.
  const computeEndDate = (startDate, course, classTypeValue, durationHours) => {
    if (!startDate || !course) return '';
    const start = new Date(startDate);
    if (isNaN(start.getTime())) return '';

    const classType = classTypes.find(ct => ct.value === classTypeValue);
    const sessionHours = parseFloat(durationHours) || 2;
    const totalHours = course.totalHours || (course.numberOfMonths ? course.numberOfMonths * 4 * (classType?.daysPerWeek || 5) * sessionHours : 0);

    if (classType?.daysPerWeek && sessionHours > 0 && totalHours > 0) {
      const sessions = Math.ceil(totalHours / sessionHours);
      // Sessions per week × ~7 days / sessions-per-week = actual calendar days
      const calendarDays = Math.ceil(sessions * (7 / classType.daysPerWeek));
      const end = new Date(start);
      end.setDate(start.getDate() + calendarDays - 1);
      return end.toISOString().split('T')[0];
    }
    if (course.numberOfMonths) {
      const end = new Date(start);
      end.setMonth(end.getMonth() + course.numberOfMonths);
      end.setDate(end.getDate() - 1);
      return end.toISOString().split('T')[0];
    }
    return '';
  };

  const handleCourseChange = async (courseId) => {
    const c = courses.find(x => x._id === courseId);
    setSelectedCourse(c || null);
    setForm(f => ({
      ...f,
      course: courseId,
      batchNumber: '',
      endDate: c && f.startDate ? computeEndDate(f.startDate, c, f.classType, (() => { const [sh,sm]=(f.timingStartTime||'09:00').split(':').map(Number); const [eh,em]=(f.timingEndTime||'11:00').split(':').map(Number); return Math.max(0,(eh*60+em-sh*60-sm)/60); })()) : '',
    }));
    if (courseId && form.startDate) fetchBatchNumber(courseId, form.startDate);
  };

  const fetchBatchNumber = async (courseId, startDate) => {
    if (!courseId || !startDate) return;
    setFetchingBatchNum(true);
    try {
      const r = await api.get(`/training/batches/next-batch-number/${courseId}`, { params: { startDate } });
      setForm(f => ({ ...f, batchNumber: r.data.data?.batchNumber || '' }));
    } catch(_) {}
    setFetchingBatchNum(false);
  };

  const handleSave = async () => {
    setFormError('');
    if (!form.course)    { setFormError('Course is required'); return; }
    if (!form.startDate) { setFormError('Start date is required'); return; }
    setSaving(true);
    try {
      const payload = {
        course:     form.course,
        batchNumber: form.batchNumber || undefined,
        faculty:    form.useFacultyInput ? null : (form.faculty || null),
        facultyName: form.useFacultyInput ? form.facultyName : '',
        batchType:  form.batchType,
        classType:  form.classType,
        classroom:  form.classroom,
        timing: {
          startTime: form.timingStartTime,
          endTime:   form.timingEndTime,
          durationHours: (() => {
            const [sh, sm] = (form.timingStartTime || '09:00').split(':').map(Number);
            const [eh, em] = (form.timingEndTime   || '11:00').split(':').map(Number);
            return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
          })(),
        },
        startDate:   form.startDate,
        endDate:     form.endDate || undefined,
        maxStudents: parseInt(form.maxStudents) || 30,
        status:      form.status,
        notes:       form.notes,
        schedule:    form.schedule,
      };
      if (editing) await api.put(`/training/batches/${editing._id}`, payload);
      else          await api.post('/training/batches', payload);
      notify(`Batch ${editing ? 'updated' : 'created'}!`);
      setShowForm(false); loadBatches();
    } catch(err) { setFormError(err.response?.data?.message || 'Error saving'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/training/batches/${deleteTarget._id}`);
      notify('Batch deleted', 'warning');
      setDeleteTarget(null); loadBatches();
    } catch(err) { notify(err.response?.data?.message || 'Delete failed', 'danger'); }
    setDeleting(false);
  };

  // Returns the batch-level start/end times for pre-filling new schedule rows.
  const defaultScheduleTimes = () => ({
    startTime: form.timingStartTime || '09:00',
    endTime:   form.timingEndTime   || '11:00',
  });

  // Schedule helpers
  const addScheduleDay = (day) => {
    if (form.schedule.some(s => s.day === day)) return;
    const { startTime, endTime } = defaultScheduleTimes();
    setForm(f => ({ ...f, schedule: [...f.schedule, { day, startTime, endTime }] }));
  };
  const removeScheduleDay = (day) => setForm(f => ({ ...f, schedule: f.schedule.filter(s => s.day !== day) }));
  const updateScheduleTime = (day, field, val) => setForm(f => ({
    ...f, schedule: f.schedule.map(s => s.day === day ? { ...s, [field]: val } : s)
  }));

  // Progress
  const openProgress = async (batch) => {
    setProgressBatch(batch); setProgressData(null); setExpandedMods({}); setShowProgress(true);
    try {
      const r = await api.get(`/training/batches/${batch._id}/progress`);
      setProgressData(r.data.data || r.data.records || r.data);
    } catch(_) { setProgressData(null); }
  };

  // Settings CRUD
  const saveClassType = async () => {
    setSettingsSaving(true);
    try {
      await api.post('/training/batch-settings/class-types', ctForm);
      setCtForm({ value:'', label:'', daysPerWeek:5 });
      loadSettings(); notify('Class type added');
    } catch(err) { notify(err.response?.data?.message || 'Error', 'danger'); }
    setSettingsSaving(false);
  };
  const toggleClassType = async (id) => {
    try { await api.patch(`/training/batch-settings/class-types/${id}/toggle`); loadSettings(); } catch(_) {}
  };
  const deleteClassType = async (id) => {
    try { await api.delete(`/training/batch-settings/class-types/${id}`); loadSettings(); } catch(_) {}
  };
  const saveClassroom = async () => {
    setSettingsSaving(true);
    try {
      await api.post('/training/batch-settings/classrooms', crForm);
      setCrForm({ name:'', capacity:30 });
      loadSettings(); notify('Classroom added');
    } catch(err) { notify(err.response?.data?.message || 'Error', 'danger'); }
    setSettingsSaving(false);
  };
  const toggleClassroom = async (id) => {
    try { await api.patch(`/training/batch-settings/classrooms/${id}/toggle`); loadSettings(); } catch(_) {}
  };
  const deleteClassroom = async (id) => {
    try { await api.delete(`/training/batch-settings/classrooms/${id}`); loadSettings(); } catch(_) {}
  };

  const buildCountOptions = (values = [], labelFormatter = null) => {
    const counts = new Map();
    values
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .forEach((value) => counts.set(value, Number(counts.get(value) || 0) + 1));
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, label: `${labelFormatter ? labelFormatter(value) : value} (${count})` }));
  };

  const pages = Math.ceil(total / LIMIT);
  const pageStart = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const pageEnd = Math.min(page * LIMIT, total);
  const batchOptions = buildCountOptions((batches || []).flatMap((b) => ([b.batchNumber, b.classroom])));
  const courseOptions = buildCountOptions((batches || []).flatMap((b) => ([b.course?.courseName || '', b.course?.courseCode || ''])));
  const facultyOptions = buildCountOptions((batches || []).map(b => b.faculty ? `${b.faculty.firstName || ''} ${b.faculty.lastName || ''}`.trim() : (b.facultyName || '')));
  const scheduleOptions = buildCountOptions((batches || []).map(b => {
    if (!b.timing?.startTime && !b.timing?.endTime) return '';
    return `${b.timing?.startTime || '-'} - ${b.timing?.endTime || '-'}`;
  }));
  const studentOptions = buildCountOptions((batches || []).map(b => `${b.currentStudents || 0}/${b.maxStudents || 30}`));
  const progressOptions = buildCountOptions((batches || []).map(b => b._progressPct !== undefined ? String(b._progressPct) : '').filter(Boolean), (value) => `${value}%`);

  const setColumnFilter = (key, value) => setColumnFilters(f => ({ ...f, [key]: value }));
  const clearColumnFilters = () => {
    setColumnFilters({
      batch: '',
      course: '',
      faculty: '',
      dateFrom: '',
      dateTo: '',
      schedule: '',
      students: '',
      progress: '',
      status: '',
    });
    setActiveHeaderFilter('');
  };

  const renderHeaderFilter = (key, label, type = 'text', options = []) => {
    const isActive = activeHeaderFilter === key;
    const hasValue = key === 'dates'
      ? !!(columnFilters.dateFrom || columnFilters.dateTo)
      : !!columnFilters[key];
    const normalizedValue = (columnFilters[key] || '').toLowerCase();
    const filteredOptions = options.filter(option => {
      if (!normalizedValue) return true;
      return String(option.label || option.value || '').toLowerCase().includes(normalizedValue);
    });
    return (
      <div className="d-inline-flex align-items-center gap-1 position-relative">
        <span>{label}</span>
        <button
          type="button"
          className={`btn btn-sm p-0 border-0 bg-transparent ${hasValue ? 'text-primary' : 'text-muted'}`}
          style={{ width: 18, height: 18, lineHeight: 1 }}
          onClick={() => setActiveHeaderFilter(cur => cur === key ? '' : key)}
          title={`Filter ${label}`}
        >
          <i className={`bi ${hasValue ? 'bi-funnel-fill' : 'bi-funnel'}`} />
        </button>
        {isActive && (
          <div
            className="position-absolute bg-white border rounded-3 shadow-sm p-2"
            style={{ top: 'calc(100% + 6px)', left: 0, minWidth: 220, zIndex: 20 }}
          >
            {type === 'select' ? (
              <select
                className="form-select form-select-sm"
                value={columnFilters[key]}
                onChange={e => setColumnFilter(key, e.target.value)}
              >
                <option value="">All</option>
                {options.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            ) : type === 'dateRange' ? (
              <div className="d-flex flex-column gap-2">
                <div>
                  <label className="form-label small text-muted mb-1">Start date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={columnFilters.dateFrom}
                    onChange={e => setColumnFilters(f => ({ ...f, dateFrom: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label small text-muted mb-1">End date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={columnFilters.dateTo}
                    onChange={e => setColumnFilters(f => ({ ...f, dateTo: e.target.value }))}
                  />
                </div>
              </div>
            ) : type === 'datalist' ? (
              <>
                <input
                  className="form-control form-control-sm"
                  placeholder={`Filter ${label.toLowerCase()}`}
                  value={columnFilters[key]}
                  onChange={e => setColumnFilter(key, e.target.value)}
                />
                <div className="list-group list-group-flush border rounded-2 mt-2 overflow-auto" style={{ maxHeight: 180 }}>
                  {filteredOptions.length ? filteredOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className="list-group-item list-group-item-action text-start py-2 px-2"
                      onClick={() => {
                        setColumnFilter(key, option.value);
                        setActiveHeaderFilter('');
                      }}
                    >
                      {option.label}
                    </button>
                  )) : (
                    <div className="small text-muted px-2 py-2">No suggestions</div>
                  )}
                </div>
              </>
            ) : (
              <input
                className="form-control form-control-sm"
                placeholder={`Filter ${label.toLowerCase()}`}
                value={columnFilters[key]}
                onChange={e => setColumnFilter(key, e.target.value)}
              />
            )}
            <div className="d-flex justify-content-between mt-2">
              <button
                type="button"
                className="btn btn-link btn-sm text-decoration-none p-0"
                onClick={() => {
                  if (key === 'dates') {
                    setColumnFilters(f => ({ ...f, dateFrom: '', dateTo: '' }));
                  } else {
                    setColumnFilter(key, '');
                  }
                }}
              >
                Clear
              </button>
              <button type="button" className="btn btn-link btn-sm text-decoration-none p-0" onClick={() => setActiveHeaderFilter('')}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <PageHeader title="Batches" icon="bi-collection"
        subtitle={`${total} total`}
        actions={
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div className="input-group" style={{ width:200 }}>
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input className="form-control" placeholder="Search batch #…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-select" style={{ width:140 }} value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {canWrite && (
              <>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowSettings(true)}>
                  <i className="bi bi-gear me-1"></i>Settings
                </button>
                <button className="btn btn-primary btn-sm" onClick={openNew}>
                  <i className="bi bi-plus-lg me-1"></i>New Batch
                </button>
              </>
            )}
          </div>
        }
      />

      {/* View toggle (Table ↔ Analytics) — analytics is management-only */}
      {canSeeAnalytics && (
        <div className="d-flex justify-content-end mb-3">
          <ViewToggle
            storageKey="batches"
            value={view}
            onChange={setView}
          />
        </div>
      )}

      {/* Analytics view (renders ABOVE the table) */}
      {canSeeAnalytics && view === 'analytics' && (
        <div className="mb-4">
          <BatchesAnalytics />
        </div>
      )}

      {(!canSeeAnalytics || view === 'table') && (
      <>
      {/* Stats */}
      <div className="row g-3 mb-3">
        {[
          { label:'Total', val:stats.total, icon:'bi-collection', color:'#6366f1' },
          { label:'Ongoing', val:stats.ongoing, icon:'bi-play-circle', color:'#22c55e' },
          { label:'Upcoming', val:stats.upcoming, icon:'bi-calendar', color:'#06b6d4' },
          { label:'Completed', val:stats.completed, icon:'bi-check-circle', color:'#8b5cf6' },
        ].map(({ label, val, icon, color }) => (
          <div key={label} className="col-6 col-md-3">
            <StatCard title={label} value={val ?? 0} icon={icon} color={color} />
          </div>
        ))}
      </div>

      {/* Year tabs */}
      {batchYears.length > 0 && (
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button className={`nav-link ${yearTab==='all'?'active':''}`} onClick={() => setYearTab('all')}>All</button>
          </li>
          {batchYears.map(y => (
            <li key={y} className="nav-item">
              <button className={`nav-link ${yearTab===String(y)?'active':''}`} onClick={() => setYearTab(String(y))}>{y}</button>
            </li>
          ))}
        </ul>
      )}

      {/* Inline batch form */}
      {showForm && (
        <div className="card mb-4 border-primary">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold"><i className="bi bi-collection me-2"></i>{editing ? 'Edit Batch' : 'New Batch'}</span>
            <button className="btn-close" onClick={() => setShowForm(false)}></button>
          </div>
          <div className="card-body">
            {formError && <div className="alert alert-danger py-2 mb-3">{formError}</div>}
            <div className="row g-3">
              {/* Course */}
              <div className="col-md-4">
                <label className="form-label">Course *</label>
                <select className="form-select" value={form.course} onChange={e => handleCourseChange(e.target.value)}>
                  <option value="">Select course…</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.courseName} ({c.courseCode})</option>)}
                </select>
                {selectedCourse?.numberOfMonths && (
                  <small className="text-muted">{selectedCourse.numberOfMonths} month course</small>
                )}
              </div>
              {/* Batch number */}
              <div className="col-md-3">
                <label className="form-label">Batch Number</label>
                <div className="input-group">
                  <input className="form-control" value={form.batchNumber} placeholder="Auto-generated"
                    onChange={e => setForm(f => ({ ...f, batchNumber: e.target.value.toUpperCase() }))} />
                  {fetchingBatchNum && <span className="input-group-text"><span className="spinner-border spinner-border-sm"></span></span>}
                </div>
              </div>
              {/* Faculty */}
              <div className="col-md-5">
                <label className="form-label d-flex align-items-center gap-2">
                  Faculty
                  <div className="form-check mb-0 ms-2">
                    <input className="form-check-input" type="checkbox" id="chkManualFaculty" checked={form.useFacultyInput}
                      onChange={e => setForm(f => ({ ...f, useFacultyInput: e.target.checked, faculty:'', facultyName:'' }))} />
                    <label className="form-check-label" htmlFor="chkManualFaculty" style={{ fontSize:12 }}>Enter name manually</label>
                  </div>
                </label>
                {form.useFacultyInput ? (
                  <input className="form-control" placeholder="Faculty name" value={form.facultyName}
                    onChange={e => setForm(f => ({ ...f, facultyName: e.target.value }))} />
                ) : (
                  <select className="form-select" value={form.faculty} onChange={e => setForm(f => ({ ...f, faculty: e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {faculty.map(u => <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>)}
                  </select>
                )}
              </div>
              {/* Type & Class */}
              <div className="col-md-3">
                <label className="form-label">Batch Type</label>
                <select className="form-select" value={form.batchType} onChange={e => setForm(f => ({ ...f, batchType: e.target.value }))}>
                  <option value="group">Group — open enrollment</option>
                  <option value="individual">Individual — 1-on-1</option>
                  <option value="corporate">Corporate — private client cohort</option>
                </select>
                <small className="text-muted">
                  {form.batchType === 'corporate' && 'Closed batch for a specific company\'s employees.'}
                  {form.batchType === 'individual' && 'One student, one trainer.'}
                  {form.batchType === 'group' && 'Open to any paying student.'}
                </small>
              </div>
              <div className="col-md-3">
                <label className="form-label">Class Type</label>
                <select className="form-select" value={form.classType}
                        onChange={e => {
                          const v = e.target.value;
                          setForm(f => ({
                            ...f,
                            classType: v,
                            endDate: selectedCourse && f.startDate ? computeEndDate(f.startDate, selectedCourse, v, (() => { const [sh,sm]=(f.timingStartTime||'09:00').split(':').map(Number); const [eh,em]=(f.timingEndTime||'11:00').split(':').map(Number); return Math.max(0,(eh*60+em-sh*60-sm)/60); })()) : f.endDate,
                          }));
                        }}>
                  <option value="">— None —</option>
                  {classTypes.filter(ct => ct.isActive).map(ct => <option key={ct._id} value={ct.value}>{ct.label}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Classroom</label>
                <select className="form-select" value={form.classroom} onChange={e => setForm(f => ({ ...f, classroom: e.target.value }))}>
                  <option value="">— None —</option>
                  {classrooms.filter(cr => cr.isActive).map(cr => <option key={cr._id} value={cr.name}>{cr.name} (cap:{cr.capacity})</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Max Students</label>
                <input type="number" className="form-control" value={form.maxStudents} min={1}
                  onChange={e => setForm(f => ({ ...f, maxStudents: e.target.value }))} />
              </div>
              {/* Timing */}
              <div className="col-md-3">
                <label className="form-label">Start Time</label>
                <input type="time" className="form-control" value={form.timingStartTime}
                  onChange={e => {
                    const v = e.target.value;
                    setForm(f => {
                      const [sh,sm] = v.split(':').map(Number);
                      const [eh,em] = (f.timingEndTime||'11:00').split(':').map(Number);
                      const dur = Math.max(0,(eh*60+em-sh*60-sm)/60);
                      return {
                        ...f,
                        timingStartTime: v,
                        endDate: selectedCourse && f.startDate ? computeEndDate(f.startDate, selectedCourse, f.classType, dur) : f.endDate,
                      };
                    });
                  }} />
              </div>
              <div className="col-md-3">
                <label className="form-label">End Time</label>
                <input type="time" className="form-control" value={form.timingEndTime}
                  onChange={e => {
                    const v = e.target.value;
                    setForm(f => {
                      const [sh,sm] = (f.timingStartTime||'09:00').split(':').map(Number);
                      const [eh,em] = v.split(':').map(Number);
                      const dur = Math.max(0,(eh*60+em-sh*60-sm)/60);
                      return {
                        ...f,
                        timingEndTime: v,
                        endDate: selectedCourse && f.startDate ? computeEndDate(f.startDate, selectedCourse, f.classType, dur) : f.endDate,
                      };
                    });
                  }} />
              </div>
              {/* Dates */}
              <div className="col-md-3">
                <label className="form-label">Start Date *</label>
                <input type="date" className="form-control" value={form.startDate}
                  onChange={e => {
                    const v = e.target.value;
                    setForm(f => {
                      const [sh,sm] = (f.timingStartTime||'09:00').split(':').map(Number);
                      const [eh,em] = (f.timingEndTime||'11:00').split(':').map(Number);
                      const dur = Math.max(0,(eh*60+em-sh*60-sm)/60);
                      return {
                        ...f,
                        startDate: v,
                        endDate: selectedCourse ? computeEndDate(v, selectedCourse, f.classType, dur) : f.endDate,
                      };
                    });
                    if (form.course) fetchBatchNumber(form.course, v);
                  }} />
              </div>
              <div className="col-md-3">
                <label className="form-label">End Date <span className="text-muted small">(auto-calculated)</span></label>
                <input type="date" className="form-control" value={form.endDate}
                  onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
                {selectedCourse && form.startDate && form.endDate && (
                  <small className="text-muted">
                    {selectedCourse.numberOfMonths ? `${selectedCourse.numberOfMonths}-month course` : ''}
                    {form.classType && ` · ${classTypes.find(ct => ct.value === form.classType)?.label || form.classType}`}
                  </small>
                )}
              </div>
              <div className="col-md-3">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                </select>
              </div>
              {/* Schedule */}
              <div className="col-12">
                <label className="form-label">Weekly Schedule</label>
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {DAYS.map(d => (
                    <button key={d} type="button"
                      className={`btn btn-sm ${form.schedule.some(s=>s.day===d) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => form.schedule.some(s=>s.day===d) ? removeScheduleDay(d) : addScheduleDay(d)}>
                      {d.slice(0,3).toUpperCase()}
                    </button>
                  ))}
                </div>
                {form.schedule.map(s => (
                  <div key={s.day} className="d-flex align-items-center gap-2 mb-1">
                    <span className="badge bg-primary" style={{ minWidth:40 }}>{s.day.slice(0,3).toUpperCase()}</span>
                    <input type="time" className="form-control form-control-sm" style={{ width:120 }} value={s.startTime}
                      onChange={e => updateScheduleTime(s.day,'startTime',e.target.value)} />
                    <span className="text-muted">to</span>
                    <input type="time" className="form-control form-control-sm" style={{ width:120 }} value={s.endTime}
                      onChange={e => updateScheduleTime(s.day,'endTime',e.target.value)} />
                    <button className="btn btn-outline-danger btn-sm" onClick={() => removeScheduleDay(s.day)}>
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ))}
              </div>
              <div className="col-12">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="d-flex gap-2 mt-3 pt-2 border-top">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                {editing ? 'Save Changes' : 'Create Batch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{renderHeaderFilter('batch', 'Batch', 'datalist', batchOptions)}</th>
                  <th>{renderHeaderFilter('course', 'Course', 'datalist', courseOptions)}</th>
                  <th>{renderHeaderFilter('faculty', 'Faculty', 'datalist', facultyOptions)}</th>
                  <th>{renderHeaderFilter('dates', 'Dates', 'dateRange')}</th>
                  <th>{renderHeaderFilter('schedule', 'Schedule', 'datalist', scheduleOptions)}</th>
                  <th>{renderHeaderFilter('students', 'Students', 'datalist', studentOptions)}</th>
                  <th>{renderHeaderFilter('progress', 'Progress', 'datalist', progressOptions)}</th>
                  <th>{renderHeaderFilter('status', 'Status', 'select', Object.keys(STATUS_COLORS).map(s => ({ value: s, label: s })))}</th>
                  <th className="text-end">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      type="button"
                      onClick={clearColumnFilters}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBatches.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-4 text-muted">No batches found</td></tr>
                ) : filteredBatches.map(b => (
                  <tr key={b._id}>
                    <td>
                      <div className="fw-medium">{b.batchNumber}</div>
                      {b.classroom && <small className="text-muted">{b.classroom}</small>}
                    </td>
                    <td>
                      <CourseNameBadge courseName={b.course?.courseName || '—'} courseCode={b.course?.courseCode || ''} courseColor={b.course?.courseColor} compact />
                    </td>
                    <td style={{ fontSize:13 }}>
                      {b.faculty ? `${b.faculty.firstName} ${b.faculty.lastName}` : (b.facultyName || '—')}
                    </td>
                    <td style={{ fontSize:12 }}>
                      <div>{b.startDate ? new Date(b.startDate).toLocaleDateString() : '—'}</div>
                      {b.endDate && <div className="text-muted">→ {new Date(b.endDate).toLocaleDateString()}</div>}
                    </td>
                    <td style={{ fontSize:12 }}>
                      {b.schedule?.length > 0
                        ? b.schedule.map(s => s.day?.slice(0,3).toUpperCase()).join(', ')
                        : '—'}
                      {(b.timing?.startTime || b.timing?.endTime) && (
                        <div className="text-muted">
                          {b.timing.startTime || '—'}{b.timing.endTime ? ` – ${b.timing.endTime}` : ''}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">{b.currentStudents||0}/{b.maxStudents||30}</span>
                    </td>
                    <td style={{ minWidth:100 }}>
                      {b._progressPct !== undefined ? (
                        <>
                          <div className="progress" style={{ height:6 }}>
                            <div className="progress-bar bg-success" style={{ width:`${b._progressPct}%` }}></div>
                          </div>
                          <small className="text-muted">{b._progressPct}%</small>
                        </>
                      ) : (
                        <button className="btn btn-outline-secondary btn-sm" style={{ fontSize:11 }} onClick={() => openProgress(b)}>
                          <i className="bi bi-bar-chart me-1"></i>View
                        </button>
                      )}
                    </td>
                    <td>
                      <span className={`badge bg-${STATUS_COLORS[b.status]||'secondary'}`}>{b.status}</span>
                    </td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end flex-wrap">
                        <button
                          className="btn btn-success btn-sm"
                          title="Enroll a student in this batch"
                          onClick={() => navigate(`/enrollments?batchId=${b._id}&courseId=${b.course?._id || b.course || ''}&batchNumber=${encodeURIComponent(b.batchNumber || '')}`)}
                        >
                          <i className="bi bi-person-plus me-1"></i>Enroll
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          title="View enrolled students"
                          onClick={() => navigate(`/enrollments?batchId=${b._id}&view=students`)}
                        >
                          <i className="bi bi-people me-1"></i>Students
                        </button>
                        <button className="btn btn-outline-info btn-sm" title="Progress" onClick={() => openProgress(b)}>
                          <i className="bi bi-bar-chart"></i>
                        </button>
                        {canWrite && (
                          <>
                            <button className="btn btn-outline-primary btn-sm" onClick={() => openEdit(b)}><i className="bi bi-pencil"></i></button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => setDeleteTarget(b)}><i className="bi bi-trash"></i></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            <div className="card-footer d-flex justify-content-between align-items-center py-2">
              <small className="text-muted">
                Showing {filteredBatches.length ? `${pageStart}-${Math.min(pageStart + filteredBatches.length - 1, pageEnd)}` : '0'} of {total} total
                {' '}· Page {page} of {pages}
                {' '}· {filteredBatches.length} row{filteredBatches.length === 1 ? '' : 's'} on this page
              </small>
              <div className="d-flex gap-1">
                <button className="btn btn-outline-secondary btn-sm" disabled={page<=1} onClick={() => setPage(p=>p-1)}>‹ Prev</button>
                <button className="btn btn-outline-secondary btn-sm" disabled={page>=pages} onClick={() => setPage(p=>p+1)}>Next ›</button>
              </div>
            </div>
        </div>
      )}
      </>
      )}

      {/* Progress modal */}
      <BSModal show={showProgress} onClose={() => setShowProgress(false)} title={`Progress — ${progressBatch?.batchNumber}`} size="modal-lg">
        {!progressData ? (
          <div className="text-center py-3"><span className="spinner-border text-primary"></span></div>
        ) : (
          <>
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <strong>Overall Progress</strong>
                <span className="text-muted">{progressData.completedTopics || 0}/{progressData.totalTopics || 0} topics</span>
              </div>
              <div className="progress" style={{ height:12 }}>
                <div className="progress-bar bg-success" style={{ width:`${progressData.percentage||0}%` }}></div>
              </div>
              <div className="mt-1">
                <span className={`badge bg-${progressData.percentage===100?'success':progressData.percentage>0?'info':'secondary'}`}>
                  {progressData.percentage||0}% — {progressData.status?.replace('_',' ')}
                </span>
              </div>
            </div>
            {(progressData.modules||[]).length === 0 && (
              <div className="alert alert-info py-2">No module structure defined yet. Progress will show once course content is set up.</div>
            )}
            {(progressData.modules||[]).map((mod, mi) => (
              <div key={mi} className="card mb-2">
                <div className="card-header py-2 d-flex justify-content-between align-items-center" style={{ cursor:'pointer' }}
                  onClick={() => setExpandedMods(e => ({ ...e, [mi]: !e[mi] }))}>
                  <div>
                    <i className={`bi bi-chevron-${expandedMods[mi]?'down':'right'} me-2`}></i>
                    <strong>{mod.title}</strong>
                    <span className="ms-2 badge bg-light text-dark border">{mod.completedTopics}/{mod.totalTopics}</span>
                  </div>
                  <div style={{ width:120 }}>
                    <div className="progress" style={{ height:6 }}>
                      <div className="progress-bar bg-success" style={{ width:`${mod.percentage}%` }}></div>
                    </div>
                    <small className="text-muted">{mod.percentage}%</small>
                  </div>
                </div>
                {expandedMods[mi] && (
                  <div className="card-body py-2">
                    {mod.topics?.map((t, ti) => (
                      <div key={ti} className="d-flex align-items-center gap-2 py-1 border-bottom">
                        <i className={`bi ${t.isCompleted ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'}`}></i>
                        <span style={{ fontSize:13, flex:1 }}>{t.title}</span>
                        {t.completedAt && <small className="text-muted">{new Date(t.completedAt).toLocaleDateString()}</small>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </BSModal>

      {/* Batch Settings modal */}
      <BSModal show={showSettings} onClose={() => setShowSettings(false)} title="Batch Settings" size="modal-lg"
        footer={<button className="btn btn-secondary btn-sm" onClick={() => setShowSettings(false)}>Close</button>}>
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button className={`nav-link ${settingsTab==='classTypes'?'active':''}`} onClick={() => setSettingsTab('classTypes')}>Class Types</button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${settingsTab==='classrooms'?'active':''}`} onClick={() => setSettingsTab('classrooms')}>Classrooms</button>
          </li>
        </ul>

        {settingsTab === 'classTypes' && (
          <>
            <div className="alert alert-info py-2 small mb-3">
              <i className="bi bi-info-circle me-1"></i>
              Four defaults are auto-seeded for every tenant: <code>daily</code>, <code>weekend</code>, <code>mwf</code>, <code>tts</code>.
              Add custom entries only for schedules that aren't already here.
            </div>
            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <input className={`form-control form-control-sm ${ctForm.value && classTypes.some(ct => ct.value === ctForm.value.toLowerCase()) ? 'is-invalid' : ''}`}
                  placeholder="Value (e.g. mon-wed)" value={ctForm.value}
                  onChange={e => setCtForm(f => ({ ...f, value: e.target.value }))} />
                {ctForm.value && classTypes.some(ct => ct.value === ctForm.value.toLowerCase()) && (
                  <small className="text-danger">Already exists — pick a different value.</small>
                )}
              </div>
              <div className="col-md-4">
                <input className="form-control form-control-sm" placeholder="Label (e.g. Mon · Wed)" value={ctForm.label}
                  onChange={e => setCtForm(f => ({ ...f, label: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <input type="number" className="form-control form-control-sm" placeholder="Days/week" value={ctForm.daysPerWeek} min={1} max={7}
                  onChange={e => setCtForm(f => ({ ...f, daysPerWeek: parseInt(e.target.value)||5 }))} />
              </div>
              <div className="col-md-2">
                <button className="btn btn-primary btn-sm w-100" onClick={saveClassType}
                  disabled={settingsSaving || !ctForm.value || !ctForm.label || classTypes.some(ct => ct.value === ctForm.value.toLowerCase())}>
                  {settingsSaving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-plus"></i>} Add
                </button>
              </div>
            </div>
            <table className="table table-sm">
              <thead><tr><th>Value</th><th>Label</th><th>Days/wk</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {classTypes.map(ct => (
                  <tr key={ct._id}>
                    <td><code>{ct.value}</code></td>
                    <td>{ct.label}</td>
                    <td>{ct.daysPerWeek}</td>
                    <td>
                      <span className={`badge bg-${ct.isActive?'success':'secondary'}`} style={{ cursor:'pointer' }}
                        onClick={() => toggleClassType(ct._id)}>
                        {ct.isActive?'Active':'Inactive'}
                      </span>
                    </td>
                    <td><button className="btn btn-outline-danger btn-sm" onClick={() => deleteClassType(ct._id)}><i className="bi bi-trash"></i></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {settingsTab === 'classrooms' && (
          <>
            <div className="alert alert-info py-2 small mb-3">
              <i className="bi bi-info-circle me-1"></i>
              Four classrooms are auto-seeded: <code>C1</code>, <code>C2</code>, <code>C3</code>, <code>C4</code>.
              Add rooms only if you need more than four.
            </div>
            <div className="row g-2 mb-3">
              <div className="col-md-5">
                <input className={`form-control form-control-sm ${crForm.name && classrooms.some(cr => cr.name === crForm.name.toUpperCase()) ? 'is-invalid' : ''}`}
                  placeholder="Classroom name (e.g. C5, LAB-1)" value={crForm.name}
                  onChange={e => setCrForm(f => ({ ...f, name: e.target.value }))} />
                {crForm.name && classrooms.some(cr => cr.name === crForm.name.toUpperCase()) && (
                  <small className="text-danger">"{crForm.name.toUpperCase()}" already exists — see the list below.</small>
                )}
              </div>
              <div className="col-md-4">
                <input type="number" className="form-control form-control-sm" placeholder="Capacity" value={crForm.capacity} min={1}
                  onChange={e => setCrForm(f => ({ ...f, capacity: parseInt(e.target.value)||30 }))} />
              </div>
              <div className="col-md-3">
                <button className="btn btn-primary btn-sm w-100" onClick={saveClassroom}
                  disabled={settingsSaving || !crForm.name || classrooms.some(cr => cr.name === crForm.name.toUpperCase())}>
                  {settingsSaving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-plus"></i>} Add
                </button>
              </div>
            </div>
            <table className="table table-sm">
              <thead><tr><th>Name</th><th>Capacity</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {classrooms.map(cr => (
                  <tr key={cr._id}>
                    <td>{cr.name}</td>
                    <td>{cr.capacity}</td>
                    <td>
                      <span className={`badge bg-${cr.isActive?'success':'secondary'}`} style={{ cursor:'pointer' }}
                        onClick={() => toggleClassroom(cr._id)}>
                        {cr.isActive?'Active':'Inactive'}
                      </span>
                    </td>
                    <td><button className="btn btn-outline-danger btn-sm" onClick={() => deleteClassroom(cr._id)}><i className="bi bi-trash"></i></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </BSModal>

      {/* Delete confirm */}
      <BSModal show={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Batch"
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
              {deleting && <span className="spinner-border spinner-border-sm me-1"></span>}Delete
            </button>
          </>
        }>
        <p>Delete batch <strong>{deleteTarget?.batchNumber}</strong>? This cannot be undone.</p>
      </BSModal>

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show:false }))} />
    </div>
  );
}
